// The reply for a processed step started from Other Actions (2026-10-27,
// 2026-10-30; docs/contact-view.md "Other Actions: the same step"; the
// options and the fill are manualStepReply.js's). Shown under the step in the
// Manual Panel's window, after Process: each reply's template name and a
// preview, filled exactly as it would be pasted, and one button that pastes
// it into the draft reply only when the person clicks it. onUse(text,
// option) returns true once pasted (the panel asks first over a reply that's
// in the box); nothing is ever sent from here.
import { useState } from 'react'
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material'
import { manualReplyOptions, manualReplyAmounts, manualReplyText, manualReplyPreview, pastedNote } from './manualStepReply.js'

function ReplyPreview({ text, testId }) {
  const [open, setOpen] = useState(false)
  const { short, full, more } = manualReplyPreview(text)
  return (
    <Box sx={{ mt: 0.25, p: 0.75, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography variant="caption" component="pre" data-testid={testId}
        sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}>
        {open ? full : short}
      </Typography>
      {more && (
        <Link component="button" type="button" variant="caption" onClick={() => setOpen((v) => !v)} data-testid={`${testId}-toggle`}>
          {open ? 'Show less' : 'Show all'}
        </Link>
      )}
    </Box>
  )
}

export default function ManualStepReply({ check = null, stepState = null, inputs = null, onUse, readWarranty = null, previewText = null, pastedAt = null, disabled = false, index = null }) {
  const [link, setLink] = useState('')
  const [busy, setBusy] = useState(false)
  if (stepState?.status !== 'success') return null
  const options = manualReplyOptions({ check, result: stepState.result })
  if (!options.length) return null
  const needsLink = options.some((o) => o.needsLink)
  const fill = (option, extra = {}) => manualReplyText(option.text, {
    erpRmaId: stepState.result?.erpRmaId ?? null,
    amounts: manualReplyAmounts(inputs ?? {}),
    link: (option.needsLink ? link.trim() : '') || null,
    ...extra,
  })
  const asPreview = (text) => (typeof previewText === 'function' ? previewText(text) : text)

  async function use(option) {
    setBusy(true)
    try {
      const cleanLink = option.needsLink ? link.trim() : ''
      let warranty = null
      if (cleanLink && typeof readWarranty === 'function') {
        try { warranty = (await readWarranty(cleanLink))?.warranty ?? null } catch { warranty = null }
      }
      await onUse?.(fill(option, { warranty }), option)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ mt: 0.75, flexBasis: '100%' }} data-testid="manual-step-reply" data-step-index={index ?? undefined}>
      <Typography variant="caption" fontWeight={600} display="block">The reply for this result</Typography>
      {needsLink && (
        <TextField size="small" fullWidth label="Link to the correct part" value={link} onChange={(ev) => setLink(ev.target.value)}
          disabled={disabled || busy} inputProps={{ 'data-testid': 'manual-step-reply-link' }} sx={{ mt: 0.5 }} />
      )}
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {options.map((o) => (
          <Box key={o.key} data-testid={`manual-step-reply-option-${o.key}`}>
            <Typography variant="caption" display="block" data-testid={`manual-step-reply-title-${o.key}`}>
              Template: <b>{o.title ?? 'the reply for this result'}</b>
            </Typography>
            <ReplyPreview text={asPreview(fill(o))} testId={`manual-step-reply-preview-${o.key}`} />
            <Button size="small" variant="outlined" disabled={disabled || busy} onClick={() => use(o)}
              data-testid={`manual-step-use-reply-${o.key}`} sx={{ mt: 0.5, fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}>
              {o.label}
            </Button>
          </Box>
        ))}
      </Stack>
      {pastedAt && (
        <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }} data-testid="manual-step-reply-pasted">
          {pastedNote(pastedAt)}
        </Typography>
      )}
    </Box>
  )
}
